import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  type FrontendDetectionEvent,
  FrontendDetectionGroupStatusEvent,
  type FrontendSuppressionEvent,
  useDeviceStatusSocket,
  useIntl,
} from '../../hooks';
import type { DetectionGroup } from '../../types/detection-group.js';
import { DetectionsSequencesSunburst } from './DetectionsSequencesSunburst.js';

interface MultiDeviceSunburstPanelProps {
  deviceIds: string[];
  groups: DetectionGroup[] | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export function MultiDeviceSunburstPanel({
  deviceIds,
  groups,
  isLoading,
  isFetching,
  error,
}: MultiDeviceSunburstPanelProps) {
  const intl = useIntl();

  const queryClient = useQueryClient();
  const handleNewDetection = useCallback(
    ({ deviceId }: FrontendDetectionEvent) => {
      if (!deviceId || !deviceIds.includes(deviceId)) return;

      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;
          return key[0] === 'analytics-sunburst-multi';
        },
      });
    },
    [queryClient, deviceIds],
  );

  const handleNewSuppression = useCallback(
    ({ deviceId }: FrontendSuppressionEvent) => {
      if (!deviceId || !deviceIds.includes(deviceId)) return;

      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;
          return key[0] === 'analytics-sunburst-multi';
        },
      });
    },
    [queryClient, deviceIds],
  );

  const handleDetectionGroupStatusChange = useCallback(
    ({ deviceId }: FrontendDetectionGroupStatusEvent) => {
      if (!deviceId || !deviceIds.includes(deviceId)) return;

      void queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;
          return key[0] === 'analytics-sunburst-multi';
        },
      });
    },
    [queryClient, deviceIds],
  );

  useDeviceStatusSocket(
    {
      onNewDetection: handleNewDetection,
      onNewSuppression: handleNewSuppression,
      onDetectionGroupStatusChange: handleDetectionGroupStatusChange,
    },
    deviceIds,
  );

  if (deviceIds.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-foreground">
        {intl.formatMessage({ id: 'analytics.selectDevicesToView' })}
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[400px] relative">
      {isLoading && !groups && (
        <div data-testid="loading-text" className="text-center text-foreground">
          {intl.formatMessage({ id: 'common.loading' })}
        </div>
      )}

      {error && !groups && (
        <div data-testid="loading-text" className="text-center text-destructive">
          {intl.formatMessage({ id: 'detections.errorLoadingData' })}:{' '}
          {error instanceof Error
            ? error.message
            : intl.formatMessage({ id: 'common.unknownError' })}
        </div>
      )}

      {groups && (
        <div className="relative">
          {isFetching && (
            <div
              data-testid="overlay"
              className="absolute inset-0 bg-background flex items-center justify-center z-10"
            >
              <div className="text-sm text-foreground">
                {intl.formatMessage({ id: 'common.loading' })}
              </div>
            </div>
          )}
          <DetectionsSequencesSunburst data={groups} width={700} height={700} />
        </div>
      )}
    </div>
  );
}
